/**
 * audio-alerts.js — Continuous, attention-grabbing new-ticket alerts.
 *
 * v2 — re-engineered for staff-floor reality:
 *   • Two voices: "bell" (sharp pierce) and "siren" (sweeping alarm).
 *   • Per-ticket continuous mode — keeps repeating every ~2.5s until the
 *     ticket is acknowledged (clicked / status-changed / explicit stop).
 *   • Volume pushed to gain 1.0 with a multi-oscillator stack so it cuts
 *     through pocket fabric and a noisy lobby. AudioContext primed on
 *     first user gesture (iOS/Android autoplay rules).
 *   • Public mute toggle preserved (`hh_alert_muted_v1`).
 *   • Sound choice persisted in `hh_alert_sound_v1`.
 *   • Continuous toggle persisted in `hh_alert_continuous_v1` (default ON).
 *
 * Active-alarm tracking lives in `activeAlarms`, a Map keyed by ticketId
 * so the operations workspace can stop the right ringer when staff opens
 * that ticket.
 */
const MUTE_KEY = "hh_alert_muted_v1";
const SOUND_KEY = "hh_alert_sound_v1";        // "bell" | "siren"
const CONTINUOUS_KEY = "hh_alert_continuous_v1"; // "1" | "0", default "1"

const REPEAT_MS = 1700;             // near-continuous replay; clip length leaves almost no silent gap
const MAX_DURATION_MS = 30 * 60_000; // safety cap; normal stop is when any primary/staff sees the request

let _ctx = null;
let _primed = false;
let _primeListener = null;
const activeAlarms = new Map(); // ticketId → { intervalId, timeoutId, variant }

const connectLoudOutput = (ctx, gain) => {
  try {
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -18;
    compressor.knee.value = 18;
    compressor.ratio.value = 8;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.18;
    const boost = ctx.createGain();
    boost.gain.value = 1.35;
    gain.connect(compressor).connect(boost).connect(ctx.destination);
  } catch {
    gain.connect(ctx.destination);
  }
};

const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (_ctx) return _ctx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try { _ctx = new Ctor(); } catch { _ctx = null; }
  return _ctx;
};

// ─── First-gesture priming (required by iOS Safari + Android Chrome) ─────────
export function primeAudioAlerts() {
  if (typeof window === "undefined" || _primed) return;
  if (_primeListener) return;
  _primeListener = () => {
    _primed = true;
    const ctx = getCtx();
    if (ctx && ctx.state === "suspended") { ctx.resume().catch(() => {}); }
    window.removeEventListener("pointerdown", _primeListener);
    window.removeEventListener("touchstart", _primeListener);
    window.removeEventListener("keydown", _primeListener);
    _primeListener = null;
  };
  window.addEventListener("pointerdown", _primeListener, { once: true, passive: true });
  window.addEventListener("touchstart", _primeListener, { once: true, passive: true });
  window.addEventListener("keydown", _primeListener, { once: true });
}

// ─── Settings persistence ────────────────────────────────────────────────────
const readLs = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try { return window.localStorage.getItem(key) ?? fallback; } catch { return fallback; }
};

const writeLs = (key, value) => {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(key, value); } catch { /* noop */ }
};

export function isAlertMuted() { return readLs(MUTE_KEY, "0") === "1"; }

export function setAlertMuted(muted) {
  writeLs(MUTE_KEY, muted ? "1" : "0");
  if (muted) stopAllAlerts();
  window.dispatchEvent(new CustomEvent("hh-alert-mute-changed", { detail: { muted } }));
}

export function getAlertSound() {
  const v = readLs(SOUND_KEY, "bell");
  return v === "siren" ? "siren" : "bell";
}

export function setAlertSound(variant) {
  const safe = variant === "siren" ? "siren" : "bell";
  writeLs(SOUND_KEY, safe);
  window.dispatchEvent(new CustomEvent("hh-alert-sound-changed", { detail: { sound: safe } }));
}

export function isAlertContinuous() { return readLs(CONTINUOUS_KEY, "1") === "1"; }

export function setAlertContinuous(enabled) {
  writeLs(CONTINUOUS_KEY, enabled ? "1" : "0");
  if (!enabled) stopAllAlerts();
  window.dispatchEvent(new CustomEvent("hh-alert-continuous-changed", { detail: { enabled } }));
}

// ─── Sound voices ───────────────────────────────────────────────────────────
// Both clips are ≈1.6s. Continuous mode replays every REPEAT_MS so the alarm
// feels almost continuous while still leaving a tiny browser-safe scheduling gap.

function _scheduleBell(ctx, t0, urgent) {
  // "Loud Bell" — square-wave 1100Hz pierce + sine 1650Hz overtone, repeated
  // 3 (or 5 for urgent) times. Square waves are perceived ~6 dB louder than
  // pure sines at the same gain, which is exactly what we want.
  const notes = urgent ? 5 : 3;
  const noteDur = 0.22;
  const gap = 0.07;
  for (let i = 0; i < notes; i++) {
    const start = t0 + i * (noteDur + gap);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(1.0, start + 0.015);
    gain.gain.setValueAtTime(1.0, start + noteDur - 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + noteDur);
    connectLoudOutput(ctx, gain);

    const osc1 = ctx.createOscillator();
    osc1.type = "square";
    osc1.frequency.value = 1100;
    osc1.connect(gain);
    osc1.start(start);
    osc1.stop(start + noteDur + 0.02);

    const osc2 = ctx.createOscillator();
    const g2 = ctx.createGain();
    g2.gain.value = 0.5;
    osc2.type = "sine";
    osc2.frequency.value = 1650;
    osc2.connect(g2).connect(gain);
    osc2.start(start);
    osc2.stop(start + noteDur + 0.02);

    const osc3 = ctx.createOscillator();
    const g3 = ctx.createGain();
    g3.gain.value = 0.28;
    osc3.type = "sawtooth";
    osc3.frequency.value = 735;
    osc3.connect(g3).connect(gain);
    osc3.start(start);
    osc3.stop(start + noteDur + 0.02);
  }
}

function _scheduleSiren(ctx, t0, urgent) {
  // "Urgent Siren" — pitch sweep 600 ↔ 1200Hz, twice (3x for urgent). Uses
  // sawtooth so it sounds like an emergency vehicle. Strong attention pull.
  const sweeps = urgent ? 3 : 2;
  const sweepDur = 0.55;
  for (let i = 0; i < sweeps; i++) {
    const start = t0 + i * (sweepDur + 0.05);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(1.0, start + 0.03);
    gain.gain.setValueAtTime(1.0, start + sweepDur - 0.06);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + sweepDur);
    connectLoudOutput(ctx, gain);

    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(600, start);
    osc.frequency.linearRampToValueAtTime(1200, start + sweepDur * 0.5);
    osc.frequency.linearRampToValueAtTime(600, start + sweepDur);
    osc.connect(gain);
    osc.start(start);
    osc.stop(start + sweepDur + 0.02);

    // Body — square one octave below, quieter
    const osc2 = ctx.createOscillator();
    const g2 = ctx.createGain();
    g2.gain.value = 0.4;
    osc2.type = "square";
    osc2.frequency.setValueAtTime(300, start);
    osc2.frequency.linearRampToValueAtTime(600, start + sweepDur * 0.5);
    osc2.frequency.linearRampToValueAtTime(300, start + sweepDur);
    osc2.connect(g2).connect(gain);
    osc2.start(start);
    osc2.stop(start + sweepDur + 0.02);

    const osc3 = ctx.createOscillator();
    const g3 = ctx.createGain();
    g3.gain.value = 0.25;
    osc3.type = "square";
    osc3.frequency.setValueAtTime(900, start);
    osc3.frequency.linearRampToValueAtTime(1500, start + sweepDur * 0.5);
    osc3.frequency.linearRampToValueAtTime(900, start + sweepDur);
    osc3.connect(g3).connect(gain);
    osc3.start(start);
    osc3.stop(start + sweepDur + 0.02);
  }
}

function _playOnce({ variant, urgent }) {
  const ctx = getCtx();
  if (!ctx) return;
  const schedule = () => {
    const t0 = ctx.currentTime + 0.03;
    if (variant === "siren") _scheduleSiren(ctx, t0, urgent);
    else _scheduleBell(ctx, t0, urgent);
  };
  if (ctx.state === "suspended") {
    ctx.resume().then(schedule).catch(schedule);
    return;
  }
  schedule();
}

function _vibrate(urgent) {
  if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return;
  try {
    if (urgent) navigator.vibrate([320, 90, 320, 90, 320, 90, 320]);
    else navigator.vibrate([260, 90, 260, 90, 260]);
  } catch { /* iOS Safari has no vibrate */ }
}

// ─── Public play / preview API ──────────────────────────────────────────────
/**
 * Preview a sound one time. Does not start a continuous alarm. Always plays
 * regardless of the mute toggle so users can audition voices from the settings.
 */
export function previewAlert(variant = null, { tone = "normal" } = {}) {
  const v = variant || getAlertSound();
  _playOnce({ variant: v, urgent: tone === "urgent" });
}

/**
 * Start the new-ticket alarm. If `ticketId` is provided AND continuous mode
 * is enabled, the alarm repeats every REPEAT_MS until acknowledged. Without
 * a ticketId it falls back to a single-shot play.
 *
 * @param {object} opts
 * @param {string} [opts.ticketId]
 * @param {"normal"|"urgent"} [opts.tone]
 * @param {boolean} [opts.vibrate]
 */
export function playNewTicketAlert({ ticketId = null, tone = "normal", vibrate = true } = {}) {
  if (isAlertMuted()) return false;

  const variant = getAlertSound();
  const urgent = tone === "urgent";
  const continuous = isAlertContinuous();

  // First ring — immediate
  _playOnce({ variant, urgent });
  if (vibrate) _vibrate(urgent);

  // Continuous loop (only when we have a ticketId to track / stop on)
  if (!ticketId || !continuous) return true;

  // Replace any existing alarm for this ticket (e.g. status flapped) so we
  // don't pile multiple intervals on the same ticket.
  stopAlert(ticketId);

  const intervalId = window.setInterval(() => {
    if (isAlertMuted()) {
      stopAlert(ticketId);
      return;
    }
    _playOnce({ variant: getAlertSound(), urgent });
    if (vibrate) _vibrate(urgent);
  }, REPEAT_MS);

  const timeoutId = window.setTimeout(() => stopAlert(ticketId), MAX_DURATION_MS);

  activeAlarms.set(ticketId, { intervalId, timeoutId, variant, urgent });
  window.dispatchEvent(new CustomEvent("hh-alarm-started", { detail: { ticketId } }));
  window.dispatchEvent(new CustomEvent("hh-active-alarms-changed", { detail: { count: activeAlarms.size } }));
  return true;
}

/** Stop the alarm ringing for a specific ticket. Idempotent. */
export function stopAlert(ticketId) {
  if (!ticketId) return;
  const entry = activeAlarms.get(ticketId);
  if (!entry) return;
  try { window.clearInterval(entry.intervalId); } catch { /* noop */ }
  try { window.clearTimeout(entry.timeoutId); } catch { /* noop */ }
  activeAlarms.delete(ticketId);
  window.dispatchEvent(new CustomEvent("hh-alarm-stopped", { detail: { ticketId } }));
  window.dispatchEvent(new CustomEvent("hh-active-alarms-changed", { detail: { count: activeAlarms.size } }));
}

/** Stop every active alarm. Used by the "Stop all alarms" button + mute. */
export function stopAllAlerts() {
  const ids = Array.from(activeAlarms.keys());
  ids.forEach(stopAlert);
}

/** Subscribe to active-alarm count changes. Returns an unsubscribe fn. */
export function subscribeActiveAlarms(callback) {
  const h = (e) => callback(e.detail?.count ?? activeAlarms.size, Array.from(activeAlarms.keys()));
  window.addEventListener("hh-active-alarms-changed", h);
  // Fire once immediately so subscribers can render initial state
  callback(activeAlarms.size, Array.from(activeAlarms.keys()));
  return () => window.removeEventListener("hh-active-alarms-changed", h);
}

export function getActiveAlarmIds() { return Array.from(activeAlarms.keys()); }
